import { create } from 'zustand';
import type {
  OntologyConcept,
  OntologyRelationType,
  OntologySchema,
  CreateConceptRequest,
  UpdateConceptRequest,
  CreateRelationRequest,
  UpdateRelationRequest,
  OntologyMetaType,
  OntologyStatus,
} from '@/types/ontology';

interface OntologyState {
  concepts: OntologyConcept[];
  relations: OntologyRelationType[];
  schema: OntologySchema | null;
  loading: boolean;
  error: string | null;
  
  // Fetch operations
  fetchConcepts: (domainId: string, token?: string | null, filters?: { metaType?: OntologyMetaType; status?: OntologyStatus }) => Promise<void>;
  fetchRelations: (domainId: string, token?: string | null) => Promise<void>;
  fetchSchema: (domainId: string, token?: string | null) => Promise<void>;
  
  // Concept CRUD
  createConcept: (domainId: string, data: CreateConceptRequest, token?: string | null) => Promise<OntologyConcept>;
  updateConcept: (id: string, data: UpdateConceptRequest, token?: string | null) => Promise<void>;
  deleteConcept: (id: string, token?: string | null) => Promise<void>;
  
  // Relation CRUD
  createRelation: (domainId: string, data: CreateRelationRequest, token?: string | null) => Promise<OntologyRelationType>;
  updateRelation: (id: string, data: UpdateRelationRequest, token?: string | null) => Promise<void>;
  deleteRelation: (id: string, token?: string | null) => Promise<void>;
  
  // Utility
  clearError: () => void;
}

export const useOntologyStore = create<OntologyState>((set, get) => ({
  concepts: [],
  relations: [],
  schema: null,
  loading: false,
  error: null,

  fetchConcepts: async (domainId, token, filters) => {
    if (get().loading) return;
    set({ loading: true, error: null });
    
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const params = new URLSearchParams();
      if (filters?.metaType) params.append('metaType', filters.metaType);
      if (filters?.status) params.append('status', filters.status);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await fetch(`/api/dotnet/ontology/${domainId}/concepts${queryString}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch concepts: HTTP ${response.status}`);
      }

      const data = await response.json();
      set({ concepts: Array.isArray(data) ? data : [], loading: false });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch concepts',
        concepts: [],
      });
    }
  },

  fetchRelations: async (domainId, token) => {
    if (get().loading) return;
    set({ loading: true, error: null });
    
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`/api/dotnet/ontology/${domainId}/relations`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch relations: HTTP ${response.status}`);
      }

      const data = await response.json();
      set({ relations: Array.isArray(data) ? data : [], loading: false });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch relations',
        relations: [],
      });
    }
  },

  fetchSchema: async (domainId, token) => {
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`/api/dotnet/ontology/${domainId}/schema`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch schema: HTTP ${response.status}`);
      }

      const data = await response.json();
      set({ schema: data });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch schema',
        schema: null,
      });
    }
  },

  createConcept: async (domainId, data, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/${domainId}/concepts`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to create concept: HTTP ${response.status}`);
    }

    const newConcept = await response.json();
    set({ concepts: [...get().concepts, newConcept] });
    return newConcept;
  },

  updateConcept: async (id, data, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/concepts/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to update concept: HTTP ${response.status}`);
    }

    const updated = await response.json();
    set({
      concepts: get().concepts.map((c) => (c.id === id ? updated : c)),
    });
  },

  deleteConcept: async (id, token) => {
    const headers: HeadersInit = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/concepts/${id}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to delete concept: HTTP ${response.status}`);
    }

    set({
      concepts: get().concepts.filter((c) => c.id !== id),
    });
  },

  createRelation: async (domainId, data, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/${domainId}/relations`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to create relation: HTTP ${response.status}`);
    }

    const newRelation = await response.json();
    set({ relations: [...get().relations, newRelation] });
    return newRelation;
  },

  updateRelation: async (id, data, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/relations/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to update relation: HTTP ${response.status}`);
    }

    const updated = await response.json();
    set({
      relations: get().relations.map((r) => (r.id === id ? updated : r)),
    });
  },

  deleteRelation: async (id, token) => {
    const headers: HeadersInit = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/relations/${id}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to delete relation: HTTP ${response.status}`);
    }

    set({
      relations: get().relations.filter((r) => r.id !== id),
    });
  },

  clearError: () => set({ error: null }),
}));
